import * as React from "react";
import { useState, useEffect, useCallback } from 'react';
import { useDataProvider, useNotify, Button } from 'react-admin';
import { Box, Typography } from '@material-ui/core';

/**
 * PenaltyGroupAssignmentPanel
 *
 * Reusable component for managing a penalty group assignment on a department
 * or employment. Shows the currently assigned group (if any) with a remove
 * button, and a selector to assign a different group.
 *
 * Props:
 *   - type: 'department' | 'employment'
 *   - id: the department_id or employment_id
 *   - productionId: the production this record belongs to
 */
const PenaltyGroupAssignmentPanel = ({ type, id, productionId }) => {
    const dataProvider = useDataProvider();
    const notify       = useNotify();

    const [assignment, setAssignment]   = useState(null);
    const [groups, setGroups]           = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [loading, setLoading]         = useState(true);
    const [saving, setSaving]           = useState(false);

    // Fetch all penalty groups for this production
    const fetchGroups = useCallback(() => {
        dataProvider.getList('penalty-groups', {
            filter: {},
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
        }).then(({ data }) => {
            setGroups(data);
        }).catch(() => {
            notify('Error loading penalty groups', 'error');
        });
    }, [dataProvider]);

    // Fetch current assignment for this department or employment
    const fetchAssignment = useCallback(() => {
        setLoading(true);
        dataProvider.getList('penalty-group-assignments', {
            filter: { [`${type}_id`]: id },
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'id', order: 'ASC' },
        }).then(({ data }) => {
            setAssignment(data.length > 0 ? data[0] : null);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [dataProvider, type, id]);

    useEffect(() => {
        fetchGroups();
        fetchAssignment();
    }, [id]);

    const assignGroup = () => {
        if (!selectedGroup) {
            notify('Please select a penalty group', 'warning');
            return;
        }
        setSaving(true);

        // If there's an existing assignment, remove it first
        const doAssign = () => dataProvider.create('penalty-group-assignments', {
            data: {
                penalty_group_id: parseInt(selectedGroup),
                [`${type}_id`]: id,
            }
        }).then(() => {
            notify('Penalty group assigned', 'success');
            setSelectedGroup('');
            setSaving(false);
            fetchAssignment();
        }).catch((response) => {
            notify(response.body?.message || 'Error assigning penalty group', 'error');
            setSaving(false);
        });

        if (assignment) {
            dataProvider.delete('penalty-group-assignments', { id: assignment.id })
                .then(doAssign)
                .catch(() => {
                    notify('Error removing existing assignment', 'error');
                    setSaving(false);
                });
        } else {
            doAssign();
        }
    };

    const removeAssignment = () => {
        setSaving(true);
        dataProvider.delete('penalty-group-assignments', { id: assignment.id })
            .then(() => {
                notify('Penalty group assignment removed', 'success');
                setAssignment(null);
                setSaving(false);
            }).catch(() => {
                notify('Error removing assignment', 'error');
                setSaving(false);
            });
    };

    const assignedGroup = assignment
        ? groups.find(g => g.id === assignment.penalty_group_id)
        : null;

    if (loading) return <Typography variant="body2">Loading...</Typography>;

    return (
        <Box mt={2}>
            <Typography variant="h6" gutterBottom>Penalty Group</Typography>

            {assignedGroup ? (
                <Box display="flex" alignItems="center" style={{ gap: '1em' }} mb={1}>
                    <Typography variant="body1">
                        Assigned: <strong>{assignedGroup.name}</strong>
                    </Typography>
                    <Button
                        label="Remove"
                        onClick={removeAssignment}
                        disabled={saving}
                        style={{ color: '#f44336' }}
                    />
                </Box>
            ) : (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    No penalty group assigned — penalties will not be calculated.
                </Typography>
            )}

            <Box display="flex" alignItems="center" style={{ gap: '1em' }} mt={1}>
                <select
                    value={selectedGroup}
                    onChange={e => setSelectedGroup(e.target.value)}
                    style={{ padding: '8px', minWidth: '220px' }}
                    disabled={saving}
                >
                    <option value="">
                        {assignedGroup ? 'Change group...' : 'Select a group...'}
                    </option>
                    {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                </select>
                <Button
                    variant="contained"
                    label={assignedGroup ? 'Change' : 'Assign'}
                    onClick={assignGroup}
                    disabled={saving || !selectedGroup}
                />
            </Box>
        </Box>
    );
};

export default PenaltyGroupAssignmentPanel;